import { useState, useEffect, useContext } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { V3RegistrationForm } from 'next-gen-sdk'
import { useAuth } from 'react-oidc-context'
import { useForm, useValidatedState, useValidation } from '@eltoro-ui/hooks'
import classNames from 'classnames'
import { ErrorMessage, isValidCharLength, styleTailwind } from 'Helpers'
import { PageContent, PuertoRicoMunis } from 'Components'
import {
  Button,
  Checkbox,
  Fa,
  Form,
  FormItem,
  PreviewType,
  Text,
  TextInput,
  TextHeader,
  Uploader,
  RadioButtonGroup,
  RadioButton,
} from '@eltoro-ui/components'
import { useCampaignContext, useAppContext, ThemeContext } from 'Contexts'
import { UserType } from 'types'
import './PoliticalTransparencyPage.scss'
import { setPoliticalFieldsAndForm } from 'Requests'

//TODO: Add state of election field once the field is supported on the backend

const PolitInput = styleTailwind(
  TextInput,
  'PoliticalTransparencyInput relative flex items-center justify-center gap-2',
)
const PolSelect = styleTailwind(
  'select',
  'PoliticalTransparencySelect Select border-thin border-grey-200 disabled:hover:border-grey-200 hover:border-grey-600 focus:bg-grey-050 relative flex items-center justify-center gap-2 rounded-lg py-2 px-4 transition-all disabled:bg-transparent',
)
const PolSubTitle = styleTailwind(
  TextHeader,
  'TransparencyInfoInput__subTitle mb-2 text-l',
)
const PolInputContainer = styleTailwind(
  'div',
  'Transparency-InputContainer gap-y-2 gap-x-3',
)

const usStates = [
  'AL',
  'AK',
  'AS',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FM',
  'FL',
  'GA',
  'GU',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MH',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'MP',
  'OH',
  'OK',
  'OR',
  'PW',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VI',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]
const provinceOptions = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
]
const countryOptions = [
  {
    label: 'Canada',
    value: 'Canada',
  },
  {
    label: 'United States',
    value: 'United States of America',
  },
  {
    label: 'Puerto Rico',
    value: 'Puerto Rico',
  },
]
export type TransparencyInfoType = {
  candidate: string
  electionDate: Date
  submitterName: string
  submitterAddress: string
  submitterCity: string
  submitterState: string
  submitterZip: string
  submitterPhone: string
  sponsorName: string
  treasurerName: string
  sponsorAddress: string
  sponsorCity: string
  sponsorState: string
  sponsorZip: string
  sponsorPhone: string
  payer: string
  paymentMethod: string
  paymentOther: string
  AudiencesTargeted: string
  AudienceDesc: string
  fecID: string
  independentCommittee: boolean
  stateRegistrationForm: V3RegistrationForm
  isAccuracyAcknowledged: boolean
}
type ParamType = {
  campaignId: string
}

export const PoliticalTransparencyPage = ({
  navFunc,
}: {
  navFunc: (step: 1 | 2 | 3 | 4 | 5) => void
}) => {
  const {
    campaignServiceApi,
    currentOrg,
    isLeadershipOrDev,
    tok,
  } = useAppContext()
  const {
    campaign,
    orderLines,
    fetchDraftCampaign,
    start_date,
    end_date,
  } = useCampaignContext()
  const { name: theme } = useContext(ThemeContext)

  const auth = useAuth()
  const loc = useLocation()
  const params = useParams<ParamType>()
  const history = useNavigate()
  const [user, SetUser] = useState<UserType | undefined>()
  const [uncheckedData, setUncheckedData] = useState({
    candidate: '',
    electionDate: '',
    payer: '',
    paymentMethod: '',
    paymentOther: '',
    submitterName: '',
    treasurerName: '',
    submitterAddress: '',
    submitterCity: '',
    submitterState: '',
    submitterZip: '',
    sponsorName: '',
    sponsorAddress: '',
    sponsorCity: '',
    submitterCountry: '',
    sponsorState: '',
    sponsorZip: '',
    audiencesTargeted: '',
    audienceDesc: '',
    fecID: 'N/A',
    independentCommittee: false,
    isAccuracyAcknowledged: false,
  })

  const [stateFile, setStateFile] = useState<PreviewType>()
  const [stateOfFile, setStateOfFile] = useState<string>('none')
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [haveRegistrationNumber, setHaveRegistrationNumber] = useState<boolean>(
    false,
  )
  const [noElectionDate, setNoElectionDate] = useState<boolean>(false)

  const [
    submitterZipcode,
    setSubmitterZipcode,
    isValidSubmitterZipcode,
  ] = useValidatedState('', /^\d{5}$/)
  const isValidCanadianZip = useValidation('canadian-zip')

  const [
    sponsorZipcode,
    setSponsorZipcode,
    isValidSponsorZipcode,
  ] = useValidatedState('', /^\d{5}$/)

  const [
    sponsorPhone,
    setSponsorPhone,
    isValidSponsorPhone,
  ] = useValidatedState(
    '',
    /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
  )
  const [
    submitterPhone,
    setSubmitterPhone,
    isValidSubmitterPhone,
  ] = useValidatedState(
    '',
    /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
  )

  const {
    candidate,
    electionDate,
    payer,
    paymentMethod,
    paymentOther,
    submitterName,
    submitterAddress,
    submitterCity,
    submitterState,
    submitterCountry,
    sponsorName,
    treasurerName,
    sponsorAddress,
    sponsorCity,
    sponsorState,
    audienceDesc,
    audiencesTargeted,
    fecID,
    independentCommittee,
    isAccuracyAcknowledged,
  } = uncheckedData

  const { formData, isValidForm, touched } = useForm({
    candidate,
    electionDate,
    payer,
    paymentMethod,
    paymentOther,
    submitterName,
    submitterAddress,
    submitterCity,
    submitterState,
    submitterCountry,
    submitterPhone,
    sponsorName,
    treasurerName,
    sponsorAddress,
    sponsorCity,
    sponsorState,
    sponsorPhone,
    audienceDesc,
    audiencesTargeted,
    fecID,
    independentCommittee,
    submitterZipcode,
    sponsorZipcode,
    isAccuracyAcknowledged,
  })
  const required = [
    `candidate`,
    'electionDate',
    `payer`,
    `paymentMethod`,
    `sponsorName`,
    `treasurerName`,
    `sponsorAddress`,
    `sponsorCity`,
    `sponsorState`,
    `sponsorZipcode`,
    `sponsorPhone`,
    `submitterName`,
    `submitterAddress`,
    `submitterCity`,
    `submitterState`,
    `submitterZipcode`,
    'submitterCountry',
    'submitterPhone',
    `audienceDesc`,
    `audiencesTargeted`,
    `isAccuracyAcknowledged`,
  ]
  const { validForm, missingFields } = isValidForm([
    ...required,
    isValidSponsorZipcode,
    isValidSponsorPhone,
    isValidSubmitterPhone,
    submitterCountry !== 'Canada'
      ? isValidSubmitterZipcode
      : isValidCanadianZip(submitterZipcode),
    isValidCharLength(candidate, 255),
    isValidCharLength(sponsorName, 100),
    isValidCharLength(treasurerName, 100),
    isValidCharLength(sponsorAddress, 255),
    isValidCharLength(sponsorCity, 100),
    isValidCharLength(sponsorZipcode, 5),
    isValidCharLength(sponsorPhone, 10),
    isValidCharLength(submitterAddress, 255),
    isValidCharLength(submitterCity, 100),
    submitterCountry !== 'Canada'
      ? isValidCharLength(submitterZipcode, 5)
      : isValidCharLength(submitterZipcode, 6),
    !haveRegistrationNumber ||
      (haveRegistrationNumber && isValidCharLength(fecID, 50)),
  ])
  //refetches from url when page is reloaded and state resets
  if (params.campaignId && !campaign?.id && !loc.pathname.includes('edit')) {
    fetchDraftCampaign(params.campaignId)
  }

  useEffect(() => {
    const getUser = () => {
      SetUser(auth.user?.profile)
    }
    getUser()
  }, [])

  const validCheck = (field: string, charLengthCheck = true) => {
    if (attemptedSubmit || (field !== '' && field.length < 2)) {
      if (field && charLengthCheck) {
        return true
      }
      return false
    }
    return true
  }

  const isReadOnly =
    campaign?.politicalTransparency &&
    orderLines?.[0]?.politicalFields &&
    orderLines.every((ol) => ol.status === 'ORDERLINE_STATUS_REVIEW_PENDING')

  const editingDisabled =
    isReadOnly ||
    (!isLeadershipOrDev &&
      orderLines?.some(
        (ol) => ol.paid || ol.status !== 'ORDERLINE_STATUS_DRAFT',
      ))

  useEffect(() => {
    const campaignFields = campaign?.politicalTransparency
    const olFields = orderLines?.[0]?.politicalFields
    setUncheckedData({
      candidate: campaignFields?.politicalObjective || '',
      electionDate: campaignFields?.electionDates || '',
      payer: olFields?.paidForBy || '',
      paymentMethod: campaignFields?.paymentMethodType || '',
      paymentOther: campaignFields?.paymentMethodOther || '',
      submitterName:
        olFields?.submitterName ||
        `${user?.given_name || ''} ${user?.family_name || ''}`,
      submitterAddress: olFields?.submitterAddress || '',
      submitterCity: olFields?.submitterCity || '',
      submitterState: olFields?.submitterState || '',
      submitterZip: olFields?.submitterZip || '',
      submitterCountry: olFields?.submitterCountry || '',
      sponsorName: campaignFields?.orgName || '',
      treasurerName: campaignFields?.treasurerName || '',
      sponsorAddress: campaignFields?.orgAddress1 || '',
      sponsorCity: campaignFields?.orgCity || '',
      sponsorState: campaignFields?.orgRegion || '',
      sponsorZip: campaignFields?.orgPostalCode || '',
      audiencesTargeted: olFields?.audienceIdsTargeted || '',
      audienceDesc: olFields?.audienceDescription || '',
      isAccuracyAcknowledged: campaignFields?.isAccuracyAcknowledged || false,
      fecID: campaignFields?.usfecid || 'N/A',
      independentCommittee: campaignFields?.isExpenditureCommittee || false,
    })
    setSubmitterPhone(olFields?.submitterPhone || '')
    setSponsorPhone(campaignFields?.orgPhone || '')
    setSponsorZipcode(campaignFields?.orgPostalCode || '')
    setSubmitterZipcode(olFields?.submitterZip || '')
    setStateOfFile(campaign?.politicalIndependentExpenditureState || 'none')
    setHaveRegistrationNumber(
      !!campaignFields?.usfecid?.trim() && campaignFields.usfecid !== 'N/A',
    )
    setNoElectionDate(campaignFields?.electionDates === 'N/A')
  }, [
    campaign?.id,
    campaign?.politicalTransparency,
    campaign?.politicalIndependentExpenditureState,
    orderLines,
    setSponsorPhone,
    setSponsorZipcode,
    setSubmitterZipcode,
    setSubmitterPhone,
    user?.given_name,
    user?.family_name,
  ])

  const onSubmit = async () => {
    if (
      currentOrg?.id &&
      campaign?.id &&
      campaign.orgId &&
      campaignServiceApi
    ) {
      await setPoliticalFieldsAndForm(
        campaign.id,
        campaign.orgId,
        {
          election_dates: formData?.electionDate || '',
          is_accuracy_acknowledged:
            uncheckedData?.isAccuracyAcknowledged || false,
          is_expenditure_committee: formData?.independentCommittee,
          org_address_1: formData?.sponsorAddress,
          org_city: formData?.sponsorCity,
          org_country: 'United States',
          org_name: formData?.sponsorName,
          org_phone: formData?.sponsorPhone,
          org_phone_code: '+1(United States)',
          org_postal_code: formData?.sponsorZipcode,
          org_region: formData?.sponsorState,
          payment_method_other: formData?.paymentOther,
          payment_method_type: formData?.paymentMethod,
          political_objective: formData?.candidate,
          treasurer_name: formData?.treasurerName,
          usfecid: formData?.fecID,
          // government_level: 'federal',
        },
        {
          audience_description: formData.audienceDesc,
          audience_ids_targeted: formData.audiencesTargeted,
          org_zip: formData.sponsorZipcode,
          paid_for_by: formData.payer,
          submitter_name: formData.submitterName,
          submitter_address: formData.submitterAddress,
          submitter_city: formData.submitterCity,
          submitter_state: formData.submitterState,
          submitter_zip: formData.submitterZipcode,
          submitter_country: formData.submitterCountry,
          submitter_phone: formData.submitterPhone,
        },
        stateFile,
        stateOfFile !== 'none' && stateFile ? stateOfFile : undefined,
        tok,
      )
      // if no order line, create one
      if (orderLines && orderLines.length === 0) {
        await campaignServiceApi.advertisingPlatformServiceCreateOrderLine(
          currentOrg?.id,
          {
            name: `${campaign?.name}_Draft_${1}`,
            startTime: new Date(start_date),
            endTime: new Date(end_date),
            impressions: currentOrg?.minimumImpressions,
            political: true,
            politicalFields: {
              audienceDescription: formData.audienceDesc,
              audienceIdsTargeted: formData.audiencesTargeted,
              orgZip: formData.sponsorZipcode,
              paidForBy: formData.payer,
              submitterName: formData.submitterName,
              submitterAddress: formData.submitterAddress,
              submitterCity: formData.submitterCity,
              submitterState: formData.submitterState,
              submitterZip: formData.submitterZipcode,
              submitterCountry: formData.submitterCountry,
              submitterPhone: formData.submitterPhone,
            },
          },
          campaign?.id,
        )
      }
      const query = currentOrg?.id ? `?org_id=${currentOrg.id}` : ''
      history(
        `../${loc.pathname.includes('edit') ? 'edit' : 'create'}/${
          campaign?.id
        }/settings${query}`,
      )
      navFunc(4)
      fetchDraftCampaign(campaign?.id)
    }
  }
  const UploaderTextDeterminer = () => {
    if (!campaign?.politicalIndependentExpenditureFormPath && !stateFile) {
      // PDF is not in S3
      return (
        <>
          <Text
            tag="p"
            className="border-tint-gray-200 border-b-thin w-128 mb-1 pt-4 pb-2 text-center text-xl font-normal uppercase"
          >
            New York and New Jersey Sponsors
          </Text>
          <Text tag="p" className="text-center text-lg font-normal">
            If your advertisement relates to a state, county, or local election,
            state law requires El Toro to maintain a copy of your state election
            registration. First select the state above, and then upload
            registration here.
          </Text>
          <Text tag="h4" className="flex items-center gap-2 pb-6 font-light">
            Drag and drop file here &shy;or click to upload
          </Text>
        </>
      )
    }
    if (!campaign?.politicalIndependentExpenditureFormPath && stateFile) {
      return (
        <>
          <div className="bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800 mb-4 rounded-lg border p-4">
            <Text
              tag="h3"
              className="text-success-700 dark:text-success-200 mb-2 flex items-center gap-2 text-xl font-semibold"
            >
              <Fa
                icon="check-circle"
                size={1}
                className="text-success-600 dark:text-success-300"
              />
              File Ready for Submission
            </Text>
            <Text
              tag="p"
              className={`${
                theme === 'light' ? 'text-gray-900' : 'dark:text-success-200'
              } mb-1 font-medium`}
            >
              Selected file: <span className="font-bold">{stateFile.path}</span>
            </Text>
            <Text
              tag="p"
              className="text-success-600 dark:text-success-300 text-sm"
            >
              This file will be uploaded when you submit the form.
            </Text>
          </div>
          <Text
            tag="p"
            className="text-center text-sm font-normal text-gray-600 dark:text-gray-400"
          >
            Need a different file? Drag and drop here or click to browse
          </Text>
        </>
      )
    }
    if (
      campaign?.politicalIndependentExpenditureFormPath &&
      campaign.politicalIndependentExpenditureState &&
      !stateFile
    ) {
      return (
        <>
          <div className="bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 mb-4 rounded-lg border p-4">
            <Text
              tag="h3"
              className="text-primary-700 dark:text-primary-200 mb-2 flex items-center gap-2 text-xl font-semibold"
            >
              <Fa
                icon="file-check"
                size={1}
                className="text-primary-600 dark:text-primary-300"
              />
              {campaign.politicalIndependentExpenditureState === 'NJ'
                ? 'New Jersey'
                : 'New York'}{' '}
              Registration On File
            </Text>
            <Text
              tag="p"
              className={`${
                theme === 'light' ? 'text-primary-600' : 'dark:text-primary-300'
              }`}
            >
              A{' '}
              {campaign.politicalIndependentExpenditureState === 'NJ'
                ? 'New Jersey'
                : 'New York'}{' '}
              state election registration form has been successfully attached to
              this campaign.
            </Text>
          </div>
          <Text
            tag="p"
            className="text-center text-sm font-normal text-gray-600 dark:text-gray-300"
          >
            Want to replace the current file? Drag and drop here or click to
            browse
          </Text>
        </>
      )
    }
    if (
      campaign?.politicalIndependentExpenditureFormPath &&
      campaign.politicalIndependentExpenditureState &&
      stateFile
    ) {
      return (
        <>
          <div className="bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800 mb-4 rounded-lg border p-4">
            <Text
              tag="h3"
              className="text-warning-700 dark:text-warning-200 mb-2 flex items-center gap-2 text-xl font-semibold"
            >
              <Fa
                icon="exclamation-triangle"
                size={1}
                className="text-warning-600 dark:text-warning-300"
              />
              File Replacement Ready
            </Text>
            <Text
              tag="p"
              className={`${
                theme === 'light' ? 'text-warning-600' : 'dark:text-warning-300'
              } font-medium" mb-1`}
            >
              New file selected:{' '}
              <span className="font-bold">{stateFile.path}</span>
            </Text>
            <Text
              tag="p"
              className="text-warning-600 dark:text-warning-300 text-sm"
            >
              <strong>Warning:</strong> Submitting will replace your current
              state registration file.
            </Text>
          </div>
          <Text
            tag="p"
            className="text-center text-sm font-normal text-gray-600 dark:text-gray-300"
          >
            Need a different file? Drag and drop here or click to browse
          </Text>
        </>
      )
    }
    return <></>
  }

  return (
    <PageContent className="TransparencyContainer flex items-center">
      <div className="TransparencyInfoInput m-auto">
        {!validForm && missingFields.length > 0 && attemptedSubmit && (
          <div className="TransparencyPrompt bg-warning-100 animate-slidedown relative z-30 -mx-6 mb-6 -mt-2 flex items-center justify-center gap-x-3 p-3">
            <Fa
              className="text-danger-400"
              icon="circle-exclamation"
              size={4}
            />
            <Text className="text-warning-600 text-xl">
              Please fill out all required
              <span className="text-danger-600">* </span>fields in the form
              below
            </Text>
          </div>
        )}
        <Form
          className={`PoliticalTransparencyForm flex flex-col ${
            !validForm && missingFields.length > 0 && attemptedSubmit
              ? 'PoliticalTransparencyForm__error'
              : ''
          }`}
          onSubmit={onSubmit}
          required={required}
          valid={validForm}
          touched={touched}
          missingFields={missingFields}
        >
          <div className="Transparency_subHeader-container bg-primary-100 border-t-thin border-tint-gray-300 border-r-thin border-l-thin flex flex-col rounded-t-md p-7">
            <TextHeader className="mb-2 text-3xl" type={3}>
              Transparency form
            </TextHeader>
            <Text
              className="Transparency_subHeader-text my-0 flex flex-col gap-1 py-0 text-lg"
              tag="p"
            >
              To ensure compliance with federal and state laws regarding record
              keeping and reporting requirements, as well as the policies of our
              upstream partners, El Toro requires the following information from
              you before you submit your election campaign advertisements.
              <br />
              <strong>Instructions:</strong> You must accurately complete each
              field. If a field is not applicable, please indicate “N/A” in the
              space provided. Failure to complete each field with accurate
              information is a violation of El Toro’s terms of service and may
              otherwise violate local, state, or federal law.
              <span className="font-semibold">
                Information cannot be changed after you submit the campaign.
                Please review and confirm the information is correct.
              </span>
            </Text>
          </div>
          {!haveRegistrationNumber && (
            <div
              className="Transparency_Banner_Legal_Review_Required p-6 text-white"
              style={{ backgroundColor: '#9f0712' }}
            >
              <div className="flex items-center gap-2">
                <Fa icon={'circle-exclamation'} size={2} />
                <TextHeader type={3}>Attention</TextHeader>
              </div>
              <Text
                className="Transparency_Banner_Legal_Review_Message mt-2"
                tag="p"
              >
                This form does not include a FEC or State Election Registration
                number. The order line requires additional review before it can
                be approved and deployed. Our support team will reach out to you
                if more information is needed.
              </Text>
            </div>
          )}
          <div className="Transparency__formFields-wrap border-r-thin border-l-thin border-b-thin border-tint-gray-300 flex flex-col gap-y-4 rounded-b-md pt-2 pb-10">
            <PolInputContainer className="CandidateRow">
              <PolSubTitle>
                The candidate or ballot initiative that is supported or opposed
              </PolSubTitle>
              <FormItem
                required
                className="col-half"
                htmlFor="candidate"
                label="Name of each candidate and ballot measure:"
                errorMessage={ErrorMessage({
                  fieldName: candidate,
                  max: 255,
                  label: "Candidate's name or Ballot initiative",
                })}
                valid={
                  validCheck(candidate) && isValidCharLength(candidate, 255)
                }
                counter={candidate.length > 0}
              >
                <PolitInput
                  name="candidate"
                  disabled={editingDisabled}
                  value={candidate}
                  maxLength={256}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      candidate: e.target.value,
                    })
                  }}
                />
              </FormItem>
              <FormItem
                className="col-half"
                htmlFor="electionDate"
                label="Election Date"
                required
              >
                <div className="flex items-center gap-2">
                  <PolitInput
                    name="electionDate"
                    className={`electionDate justify-start ${
                      noElectionDate ? 'opacity-20' : ''
                    }`}
                    disabled={editingDisabled || noElectionDate}
                    type="date"
                    value={electionDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUncheckedData({
                        ...uncheckedData,
                        electionDate: e.target.value,
                      })
                    }
                  />
                </div>
                <div onClick={(e) => e.stopPropagation()} className="py-1">
                  <Checkbox
                    disabled={editingDisabled}
                    className="electionDate"
                    checked={noElectionDate}
                    onChange={() => {
                      setNoElectionDate((prev) => {
                        const newValue = !prev
                        setUncheckedData({
                          ...uncheckedData,
                          electionDate: newValue ? 'N/A' : '',
                        })
                        return newValue
                      })
                    }}
                    label={
                      <Text className="whitespace-nowrap text-sm">N/A</Text>
                    }
                  />
                </div>
              </FormItem>
              {
                //! This state of election field was currently rolled back given the data field on the object isn't ready
                //! once the endpoint is ready you can uncomment this and hook up the state of election field
                /* <FormItem
                required
                htmlFor={'stateOfElection'}
                className="col-full SelectWrap"
                label="State of Election"
                errorMessage="Please select a state of election"
              >
                <PolSelect
                  name="stateOfElection"
                  className="bg-base"
                  value={electionState  || ""}
                  disabled={editingDisabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      electionState: e.target.value,
                    })
                  }}
                >
                  <option disabled key="empty-state" value=""></option>
                  <option key="federal-state" value="federal">
                    Federal
                  </option>
                  {usStates.map((state: string, index: number) => {
                    return (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    )
                  })}
                </PolSelect>
              </FormItem> */
              }
            </PolInputContainer>
            {/* ------------------------------------------------------------------------------------------------------------ */}
            {/* Sponsor INFO */}
            {/* ------------------------------------------------------------------------------------------------------------ */}
            <PolInputContainer className="SponsorRow">
              <PolSubTitle>
                The name and address of the person(s)/organization who sponsored
                the advertising
              </PolSubTitle>
              <FormItem
                required
                className="col-half"
                htmlFor="sponsorName"
                label="Name"
                errorMessage={ErrorMessage({
                  fieldName: sponsorName,
                  max: 100,
                  label: "Sponsor's name",
                })}
                valid={
                  validCheck(sponsorName) && isValidCharLength(sponsorName, 100)
                }
                counter={sponsorName.length > 0}
              >
                <PolitInput
                  disabled={editingDisabled}
                  value={sponsorName}
                  maxLength={101}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      sponsorName: e.target.value,
                    })
                  }}
                />
              </FormItem>
              <FormItem
                required
                className="col-half"
                htmlFor="treasurerName"
                label="Treasurer"
                errorMessage={ErrorMessage({
                  fieldName: treasurerName || '',
                  max: 100,
                  label: "Treasurer's name",
                })}
                valid={
                  validCheck(treasurerName) &&
                  isValidCharLength(treasurerName, 100)
                }
                counter={treasurerName.length > 0}
              >
                <PolitInput
                  name="treasurerName"
                  disabled={editingDisabled}
                  value={treasurerName}
                  maxLength={101}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      treasurerName: e.target.value,
                    })
                  }}
                />
              </FormItem>

              <FormItem
                required
                className="col-half"
                htmlFor="sponsorAddress"
                label="Address"
                errorMessage={ErrorMessage({
                  fieldName: sponsorAddress || '',
                  max: 255,
                  label: "Sponsor's address",
                })}
                valid={
                  validCheck(sponsorAddress) &&
                  isValidCharLength(sponsorAddress, 255)
                }
                counter={sponsorAddress.length > 0}
              >
                <PolitInput
                  name="sponsorAddress"
                  disabled={editingDisabled}
                  value={sponsorAddress}
                  maxLength={256}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      sponsorAddress: e.target.value,
                    })
                  }}
                />
              </FormItem>

              <FormItem
                required
                className="col-half"
                htmlFor="sponsorCity"
                label="City"
                errorMessage={ErrorMessage({
                  fieldName: sponsorCity || '',
                  max: 100,
                  label: "Sponsor's city",
                  isValid: isValidCharLength(sponsorCity, 100),
                })}
                valid={
                  validCheck(sponsorCity) && isValidCharLength(sponsorCity, 100)
                }
                counter={sponsorCity.length > 0}
              >
                <PolitInput
                  name="sponsorCity"
                  disabled={editingDisabled}
                  maxLength={101}
                  value={sponsorCity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      sponsorCity: e.target.value,
                    })
                  }}
                />
              </FormItem>

              <FormItem
                required
                className="col-quarter SelectWrap"
                htmlFor="sponsorState"
                label="State"
                errorMessage="Please select a state"
                valid={validCheck(sponsorState)}
              >
                <PolSelect
                  name="sponsorState"
                  className="bg-base"
                  disabled={editingDisabled}
                  value={sponsorState || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      sponsorState: e.target.value,
                    })
                  }
                >
                  <option disabled value=""></option>
                  {usStates.map((state: string, index: number) => {
                    return (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    )
                  })}
                </PolSelect>
              </FormItem>

              <FormItem
                required
                className="col-quarter zipcode"
                htmlFor="sponsorZipcode"
                label="ZIP code"
                errorMessage={ErrorMessage({
                  fieldName: sponsorZipcode,
                  max: 5,
                  label: "Sponsor's ZIP code",
                  isValid: isValidSponsorZipcode,
                })}
                valid={
                  validCheck(sponsorZipcode) &&
                  isValidCharLength(sponsorZipcode, 5) &&
                  isValidSponsorZipcode
                }
                counter={sponsorZipcode.length > 0}
              >
                <PolitInput
                  name="sponsorZipcode"
                  disabled={editingDisabled}
                  maxLength={6}
                  // safari handles number as alphanumerical field change to tel so the FormItem's handler will prevent alpha chars.
                  type="tel"
                  value={sponsorZipcode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSponsorZipcode(e.target.value)
                  }}
                />
              </FormItem>
              <FormItem
                required
                className="col-half"
                htmlFor="sponsorPhone"
                label="Phone"
                errorMessage={ErrorMessage({
                  fieldName: sponsorPhone || '',
                  max: 10,
                  label: "Sponsor's phone number",
                  isValid: isValidSponsorPhone,
                })}
                valid={
                  validCheck(sponsorPhone) &&
                  isValidCharLength(sponsorPhone, 10) &&
                  isValidSponsorPhone
                }
                counter={sponsorPhone.length > 0}
              >
                <PolitInput
                  name="sponsorPhone"
                  disabled={editingDisabled}
                  maxLength={11}
                  type="tel"
                  value={sponsorPhone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSponsorPhone(e.target.value)
                  }}
                />
              </FormItem>
            </PolInputContainer>
            {/* ------------------------------------------------------------------------------------------------------------ */}
            {/* SUBMITTER INFO */}
            {/* ------------------------------------------------------------------------------------------------------------ */}
            <PolInputContainer className="SubmitterRow">
              <PolSubTitle>
                The name and address of person submitting the campaign
              </PolSubTitle>
              <FormItem
                required
                className="col-half"
                htmlFor="submitterName"
                label="Name"
                errorMessage={ErrorMessage({
                  fieldName: submitterName || '',
                  max: 255,
                  label: "Submitter's name",
                })}
                valid={validCheck(submitterName)}
                counter={submitterName.length > 0}
              >
                <PolitInput
                  name="submitterName"
                  value={submitterName}
                  maxLength={256}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      submitterName: e.target.value,
                    })
                  }}
                />
              </FormItem>
              <FormItem
                required
                className="col-half"
                htmlFor="submitterAddress"
                label="Address"
                errorMessage={ErrorMessage({
                  fieldName: submitterAddress || '',
                  max: 255,
                  label: "Submitter's address",
                })}
                valid={
                  validCheck(submitterAddress) &&
                  isValidCharLength(submitterAddress, 255)
                }
                counter={submitterAddress.length > 0}
              >
                <PolitInput
                  name="submitterAddress"
                  disabled={editingDisabled}
                  value={submitterAddress}
                  maxLength={256}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      submitterAddress: e.target.value,
                    })
                  }
                />
              </FormItem>
              <FormItem
                required
                className="col-half"
                htmlFor="submitterCity"
                label="City"
                errorMessage={ErrorMessage({
                  fieldName: submitterCity || '',
                  max: 100,
                  label: "Submitter's city",
                })}
                valid={
                  validCheck(submitterCity) &&
                  isValidCharLength(submitterCity, 100)
                }
                counter={submitterCity.length > 0}
              >
                <PolitInput
                  name="submitterCity"
                  disabled={editingDisabled}
                  maxLength={101}
                  value={submitterCity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      submitterCity: e.target.value,
                    })
                  }
                />
              </FormItem>
              {submitterCountry === 'United States of America' && (
                <FormItem
                  required
                  className="col-quarter SelectWrap"
                  htmlFor="submitterState"
                  label="State"
                  errorMessage="Please select a state"
                  valid={validCheck(submitterState)}
                >
                  <PolSelect
                    name="submitterState"
                    className="bg-base"
                    disabled={editingDisabled}
                    value={submitterState || ''}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setUncheckedData({
                        ...uncheckedData,
                        submitterState: e.target.value,
                      })
                    }}
                  >
                    <option disabled value=""></option>
                    {usStates.map((state: string, index: number) => {
                      return (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      )
                    })}
                  </PolSelect>
                </FormItem>
              )}
              {submitterCountry === 'Canada' && (
                <FormItem
                  required
                  className="col-quarter SelectWrap"
                  htmlFor="submitterState"
                  label="Province"
                  errorMessage="Please select a province"
                  valid={validCheck(submitterState)}
                >
                  <PolSelect
                    name="submitterState"
                    className="bg-base"
                    disabled={editingDisabled}
                    value={submitterState || ''}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setUncheckedData({
                        ...uncheckedData,
                        submitterState: e.target.value,
                      })
                    }}
                  >
                    <option disabled value=""></option>
                    {provinceOptions.map(
                      (
                        province: { label: string; value: string },
                        index: number,
                      ) => {
                        return (
                          <option key={index} value={province.value}>
                            {province.value}
                          </option>
                        )
                      },
                    )}
                  </PolSelect>
                </FormItem>
              )}
              {submitterCountry === 'Puerto Rico' && (
                <FormItem
                  required
                  className="col-quarter SelectWrap"
                  htmlFor="submitterState"
                  label="Municipality"
                  errorMessage="Please select a municipality"
                  valid={validCheck(submitterState)}
                >
                  <PolSelect
                    name="submitterState"
                    className="bg-base"
                    disabled={editingDisabled}
                    value={submitterState || ''}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setUncheckedData({
                        ...uncheckedData,
                        submitterState: e.target.value,
                      })
                    }}
                  >
                    <option disabled value=""></option>
                    {PuertoRicoMunis.map(
                      (
                        municipality: { label: string; value: string },
                        index: number,
                      ) => {
                        return (
                          <option key={index} value={municipality.value}>
                            {municipality.value}
                          </option>
                        )
                      },
                    )}
                  </PolSelect>
                </FormItem>
              )}
              {submitterCountry !== 'Canada' ? (
                <FormItem
                  required
                  className="col-half zipcode"
                  htmlFor="submitterZipcode"
                  label="ZIP code"
                  errorMessage={ErrorMessage({
                    fieldName: submitterZipcode || '',
                    max: 5,
                    label: "Submitter's ZIP code",
                    isValid: isValidSubmitterZipcode,
                  })}
                  valid={
                    validCheck(submitterZipcode) &&
                    isValidCharLength(submitterZipcode, 5) &&
                    isValidSubmitterZipcode
                  }
                  counter={submitterZipcode.length > 0}
                >
                  <PolitInput
                    name="submitterZipcode"
                    disabled={editingDisabled}
                    value={submitterZipcode}
                    // safari handles number as alphanumerical field change to tel so the FormItem's handler will prevent alpha chars.
                    type="tel"
                    maxLength={6}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSubmitterZipcode(e.target.value)
                    }
                  />
                </FormItem>
              ) : (
                <FormItem
                  required
                  className="col-half zipcode"
                  htmlFor="submitterZipcode"
                  label="Postal code"
                  errorMessage={ErrorMessage({
                    fieldName: submitterZipcode || '',
                    max: 6,
                    label: "Submitter's Postal code",
                    isValid: isValidCanadianZip(submitterZipcode),
                  })}
                  valid={
                    validCheck(submitterZipcode) &&
                    isValidCharLength(submitterZipcode, 6) &&
                    isValidCanadianZip(submitterZipcode)
                  }
                  counter={submitterZipcode.length > 0}
                >
                  <PolitInput
                    name="submitterZipcode"
                    disabled={editingDisabled}
                    value={submitterZipcode}
                    // safari handles number as alphanumerical field change to tel so the FormItem's handler will prevent alpha chars.
                    type="text"
                    maxLength={7}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSubmitterZipcode(e.target.value)
                    }
                  />
                </FormItem>
              )}
              <FormItem
                required
                className="col-half SelectWrap"
                htmlFor="submitterCountry"
                label="Country"
                errorMessage="Please select a country"
                valid={validCheck(submitterCountry)}
              >
                <PolSelect
                  name="submitterCountry"
                  className="bg-base"
                  disabled={editingDisabled}
                  value={submitterCountry || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setUncheckedData({
                      ...uncheckedData,
                      submitterCountry: e.target.value,
                      submitterState: '', //Reset submitter state on change
                    })
                  }}
                >
                  <option disabled value=""></option>
                  {countryOptions.map((country, index: number) => {
                    return (
                      <option key={index} value={country.value}>
                        {country.label}
                      </option>
                    )
                  })}
                </PolSelect>
              </FormItem>
              <FormItem
                required
                className="col-half"
                htmlFor="submitterPhone"
                label="Phone"
                errorMessage={ErrorMessage({
                  fieldName: submitterPhone || '',
                  max: 10,
                  label: "Submitter's phone number",
                  isValid: isValidSubmitterPhone,
                })}
                valid={
                  validCheck(submitterPhone) &&
                  isValidCharLength(submitterPhone, 10) &&
                  isValidSubmitterPhone
                }
                counter={submitterPhone.length > 0}
              >
                <PolitInput
                  name="submitterPhone"
                  disabled={editingDisabled}
                  maxLength={11}
                  type="tel"
                  value={submitterPhone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSubmitterPhone(e.target.value)
                  }}
                />
              </FormItem>
            </PolInputContainer>
            {/* ------------------------------------------------------------------------------------------------------------ */}
            {/* COST INFO */}
            {/* ------------------------------------------------------------------------------------------------------------ */}
            <PolInputContainer className="CostRow">
              <PolSubTitle>Payment information</PolSubTitle>
              <FormItem
                required
                className="col-half"
                htmlFor="payer"
                label="Who made payment"
                errorMessage={ErrorMessage({
                  fieldName: payer,
                  max: 100,
                  label: "Payer's name",
                })}
                valid={validCheck(payer) && isValidCharLength(payer, 100)}
                counter={payer.length > 0}
              >
                <PolitInput
                  name="payer"
                  disabled={editingDisabled}
                  value={payer}
                  maxLength={101}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      payer: e.target.value,
                    })
                  }
                />
              </FormItem>

              <FormItem
                required
                className="col-half SelectWrap"
                htmlFor="paymentMethod"
                label="Method of payment"
                errorMessage="Please select method of payment used"
                valid={validCheck(paymentMethod)}
              >
                <PolSelect
                  name="paymentMethod"
                  className="bg-base PaymentMethod__select"
                  value={paymentMethod || ''}
                  disabled={editingDisabled}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      paymentMethod: e.target.value,
                    })
                  }
                >
                  <option disabled value=""></option>
                  {['Direct Debit', 'Credit Card'].map(
                    (str: string, index: number) => {
                      return (
                        <option key={index} value={str}>
                          {str}
                        </option>
                      )
                    },
                  )}
                </PolSelect>
              </FormItem>
              {/* This is not being used, but leaving commented for possible future use */}
              {/* {(uncheckedData?.paymentMethod === 'Other' ||
                formData?.paymentMethod === 'Other') && (
                <FormItem
                  required
                  className="col-half SelectWrap"
                  htmlFor="paymentOther"
                  label="Please specify"
                  errorMessage="Please specify the payment method"
                  valid={validCheck(paymentOther)}
                >
                  <PolitInput
                    classNameWrap={
                      paymentOther.length < 2 && attemptedSubmit
                        ? 'animate-glowwarning'
                        : ''
                    }
                    className={
                      paymentOther.length < 2 && attemptedSubmit
                        ? 'border-warning border-default'
                        : ''
                    }
                    disabled={editingDisabled}
                    value={paymentOther || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setUncheckedData({
                        ...uncheckedData,
                        paymentOther: e.target.value,
                      })
                    }
                  />
                </FormItem>
              )} */}
            </PolInputContainer>
            {/* ------------------------------------------------------------------------------------------------------------ */}
            {/* AUDIENCE INFO */}
            {/* ------------------------------------------------------------------------------------------------------------ */}
            <PolInputContainer className="AudienceRow">
              <PolSubTitle>
                An approximate description of the geographic locations and
                audiences to reach
              </PolSubTitle>
              <FormItem
                required
                className="col-half"
                htmlFor="audiencesTargeted"
                label="Targeted audience demographics:"
                errorMessage={ErrorMessage({
                  fieldName: audiencesTargeted,
                  max: 255,
                  label: 'Targeted audience demographics:',
                })}
                valid={
                  validCheck(audiencesTargeted) &&
                  isValidCharLength(audiencesTargeted, 255)
                }
                counter={audiencesTargeted.length > 0}
              >
                <PolitInput
                  name="audiencesTargeted"
                  disabled={editingDisabled}
                  value={audiencesTargeted}
                  maxLength={256}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      audiencesTargeted: e.target.value,
                    })
                  }
                />
              </FormItem>
              <FormItem
                required
                className="col-half"
                htmlFor="audienceDesc"
                label="Geographic location(s) targeted"
                errorMessage={ErrorMessage({
                  fieldName: audienceDesc,
                  max: 255,
                  label: 'Geographic location(s) targeted',
                })}
                valid={
                  validCheck(audienceDesc) &&
                  isValidCharLength(audienceDesc, 255)
                }
                counter={audienceDesc.length > 0}
              >
                <PolitInput
                  disabled={editingDisabled}
                  value={audienceDesc}
                  maxLength={256}
                  name="audienceDesc"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUncheckedData({
                      ...uncheckedData,
                      audienceDesc: e.target.value,
                    })
                  }
                />
              </FormItem>
            </PolInputContainer>
            {/* ------------------------------------------------------------------------------------------------------------ */}
            {/* ADDITIONAL INFO */}
            {/* ------------------------------------------------------------------------------------------------------------ */}
            {
              //? Removed this section since it seems like this is replaced with #4
            }
            <PolInputContainer className="AddInfoRow">
              <PolSubTitle>Additional Info</PolSubTitle>
              <FormItem
                required
                htmlFor="registrationNumber"
                className="col-half"
                label=""
              >
                <div className="flex items-center gap-6">
                  <Text tag="p" className="text-[1rem]">
                    Do you have a FEC or a State Election Registration Number?
                    <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2">
                      <RadioButton
                        name="haveRegistraionNumber"
                        value="Yes"
                        label="Yes"
                        className=""
                        size="s"
                        checked={haveRegistrationNumber}
                        onChange={() => {
                          setHaveRegistrationNumber(true)
                          setUncheckedData({
                            ...uncheckedData,
                            fecID: '',
                          })
                        }}
                      ></RadioButton>
                    </label>
                    <label>
                      <RadioButton
                        name="haveRegistraionNumber"
                        value="No"
                        label="No"
                        size="s"
                        className=""
                        checked={!haveRegistrationNumber}
                        onChange={() => {
                          setHaveRegistrationNumber(false)
                          setUncheckedData({
                            ...uncheckedData,
                            fecID: 'N/A',
                            independentCommittee: false,
                          })
                        }}
                      ></RadioButton>
                    </label>
                  </div>
                </div>
              </FormItem>
              {haveRegistrationNumber ? (
                <div className="flex w-full flex-col gap-6">
                  <FormItem
                    className="col-full mt-2"
                    required
                    htmlFor="fecID"
                    label="FEC or State Election Registration Number"
                    errorMessage={ErrorMessage({
                      fieldName: fecID,
                      max: 255,
                      label: 'FEC ID/State Election Registration Number',
                    })}
                    valid={validCheck(fecID) && isValidCharLength(fecID, 50)}
                    counter={fecID.length > 0}
                  >
                    <PolitInput
                      name="fecID"
                      disabled={editingDisabled}
                      value={fecID}
                      maxLength={51}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setUncheckedData({
                          ...uncheckedData,
                          fecID: e.target.value,
                        })
                      }
                    />
                  </FormItem>

                  <FormItem
                    htmlFor="IndependentCommittee"
                    className="Checkbox__fecID--wrap"
                  >
                    <Checkbox
                      disabled={editingDisabled}
                      className="Checkbox__fecID--wrap-checkbox flex h-full items-center"
                      checked={independentCommittee}
                      onChange={() =>
                        setUncheckedData({
                          ...uncheckedData,
                          independentCommittee: !independentCommittee,
                        })
                      }
                      label={
                        <Text className="whitespace-nowrap text-sm">
                          Political Independent Expenditure Committee
                        </Text>
                      }
                    />
                  </FormItem>
                </div>
              ) : null}
              <PolSubTitle>State Election Form(NY or NJ Only)</PolSubTitle>
              <RadioButtonGroup
                valueSelected={stateOfFile}
                name="State Election Form State (NY or NJ Only)"
                onChange={(stateVal) => {
                  setStateOfFile(stateVal as string)
                }}
                layout="horizontal"
                className="Political__radio-button justify-left relative z-20"
                defaultSelected={'none'}
              >
                <RadioButton value="NY" label="New York" />
                <RadioButton value="NJ" label="New Jersey" />
                <RadioButton value="none" label="None" />
              </RadioButtonGroup>
            </PolInputContainer>
            {campaign?.politicalIndependentExpenditureFormPath &&
              stateOfFile !== 'none' &&
              stateOfFile !== campaign?.politicalIndependentExpenditureState &&
              !stateFile && (
                <div className="bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800 m-auto mt-3 w-[95%] rounded-lg border p-3">
                  <Text
                    tag="p"
                    className="text-warning-700 dark:text-warning-200 flex items-start gap-2 text-sm"
                  >
                    <Fa
                      icon="exclamation-triangle"
                      size={1}
                      className="text-warning-600 dark:text-warning-300 mt-0.5 flex-shrink-0"
                    />
                    <span
                      className={`${
                        theme === 'light'
                          ? 'text-warning-700'
                          : 'text-warning-300'
                      }`}
                    >
                      <strong>Warning:</strong> You have changed the state
                      selection, but this change will not be saved unless you
                      upload a new file below (even if it's the same file that
                      was previously uploaded).
                    </span>
                  </Text>
                </div>
              )}
            {campaign?.politicalIndependentExpenditureFormPath &&
              stateOfFile === 'none' && (
                <div className="bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800 m-auto mt-3 w-[95%] rounded-lg border p-3">
                  <Text
                    tag="p"
                    className="text-danger-700 dark:text-danger-200 flex items-start gap-2 text-sm"
                  >
                    <Fa
                      icon="exclamation-circle"
                      size={1}
                      className="text-danger-600 dark:text-danger-300 mt-0.5 flex-shrink-0"
                    />
                    <span
                      className={`${
                        theme === 'light'
                          ? 'text-danger-700'
                          : 'text-danger-400'
                      }`}
                    >
                      <strong>Invalid Selection:</strong> This is an invalid
                      selection. If you need this form to be removed completely,
                      please contact{' '}
                      <a
                        href="mailto:support@eltoro.com"
                        className={`${
                          theme === 'light'
                            ? 'text-danger-600 hover:text-danger-800'
                            : 'text-danger-400 hover:text-danger-100'
                        } underline`}
                      >
                        support@eltoro.com
                      </a>
                      .
                    </span>
                  </Text>
                </div>
              )}
            <Uploader
              className={classNames('PoliticalTransparency__uploader', {
                'pointer-events-none mt-4 opacity-25':
                  isReadOnly || stateOfFile === 'none',
              })}
              accepts="pdf"
              maxFiles={1}
              onDrop={(files: PreviewType[]) => {
                setStateFile(files[0])
              }}
              disabled={stateOfFile === 'none'}
            >
              {UploaderTextDeterminer()}
            </Uploader>
          </div>
          {/* ------------------------------------------------------------------------------------------------------------ */}
          {/* DISCLOSURES AND SUBMIT */}
          {/* ------------------------------------------------------------------------------------------------------------ */}
          <div className="Transparency-disclosure p-6 text-xs leading-loose">
            <Text tag="p">
              Several state election campaign laws require companies involved in
              publishing advertisements to collect information concerning
              election campaign spending. Consequently, El Toro may be required
              to provide public reporting on your campaign to comply with these
              laws. This information will only be reported or made public if
              required by law.
            </Text>
            {/* Firewall policy required by legal */}
            <Text tag="p">
              Additionally, El Toro works with a significant number of political
              candidates, independent expenditure organizations, and campaign
              committees. As such, we have a legal and ethical responsibility to
              ensure that each digital advertising campaign is delivered in
              compliance with all federal and state laws and regulations
              regarding campaign finance and coordination of expenditures. In
              order to ensure compliance with these laws and regulations, it
              will occasionally be necessary for El Toro to implement a
              “firewall” between employees, independent contractors, direct
              clients, and end-advertisers engaged in political advertising. The
              purpose of the firewall is to ensure no apparent or actual illegal
              coordination occurs between two entities who are prohibited from
              coordinating based on law, regulation, or El Toro policy. All
              end-advertisers or potential end-advertisers are required to
              notify El Toro of any conflicts or potential conflicts under
              federal or state laws related to coordination prior to submitting
              orders for services or products to El Toro.
            </Text>
          </div>
          {!isReadOnly && (
            <div className="Transparency-SubmitContainer flex flex-col items-center justify-center gap-1">
              <div className="flex items-center gap-1 pb-5">
                <Checkbox
                  disabled={editingDisabled}
                  checked={isAccuracyAcknowledged}
                  onChange={() =>
                    setUncheckedData({
                      ...uncheckedData,
                      isAccuracyAcknowledged: !isAccuracyAcknowledged,
                    })
                  }
                  label={
                    <Text className="text-sm leading-relaxed">
                      {isAccuracyAcknowledged
                        ? 'Confirmed. All information is correct.'
                        : 'By checking this box and submitting this form, I certify, represent, and warrant to El Toro the information provided is true and accurate. I further certify, represent, and warrant that the individual or entity sponsoring the advertisement(s) is authorized and permitted to do so under applicable federal, state, or laws related to elections and/or campaign finance.'}
                    </Text>
                  }
                />
              </div>
              {editingDisabled ? (
                <div className="Transparency-SubmitMsg bg-base-info-50 text-base-info border-base-info flex items-center gap-2 rounded-lg border p-2 text-lg font-bold">
                  <Fa
                    icon="fa-circle-info"
                    size={1}
                    className="text-base-info-500"
                    type="solid"
                  />
                  Political transparency cannot be edited after campaign and
                  order lines are submitted.
                </div>
              ) : (
                <div className="ButtonGroup">
                  <Button
                    className={`PoliticalTransparency__submit-button mb-10 px-8 transition-all ${
                      isAccuracyAcknowledged && validForm
                        ? 'Button--primary ripples'
                        : ''
                    }`}
                    disabled={
                      !validForm ||
                      !isAccuracyAcknowledged ||
                      (campaign?.politicalIndependentExpenditureFormPath &&
                        stateOfFile === 'none') ||
                      (campaign?.politicalIndependentExpenditureFormPath !==
                        undefined &&
                        stateOfFile !== 'none' &&
                        stateOfFile !==
                          campaign?.politicalIndependentExpenditureState &&
                        !stateFile)
                    }
                    size="xl"
                    type="submit"
                    onClick={() => {
                      if (!attemptedSubmit) setAttemptedSubmit(true)
                    }}
                  >
                    {!isAccuracyAcknowledged && !validForm
                      ? 'Click on checkbox to confirm'
                      : 'Save transparency info'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Form>
      </div>
    </PageContent>
  )
}
